export type UploadStep = 'config' | 'sign' | 'upload' | 'ingest'

export type UploadErrorReason =
  | 'NETWORK'
  | 'FORBIDDEN'
  | 'UPLOAD_DISABLED'
  | 'UNKNOWN_USE_CASE'
  | 'INVALID_FILE'
  | 'EMPTY_FILE'
  | 'FILE_TOO_LARGE'
  | 'SIGNATURE_REJECTED'
  | 'ROUTE_UNAVAILABLE'
  | 'INVALID_REQUEST'
  | 'SERVICE'

// Problem type the file-uploader SDK returns on `/sign` when the use case
// kill switch is on (`isFileUploaderEnabled: false`).
const UPLOAD_DISABLED_PROBLEM_TYPE = '/errors/upload-v2-disabled'

// Problem type the ingest route returns on `/ingest` for an unknown use case.
const UNKNOWN_USE_CASE_PROBLEM_TYPE = '/errors/unknown-use-case'

// Problem type the ingest route returns when the record is already stored; the
// call treats it as a safe-to-retry success.
const ALREADY_INGESTED_PROBLEM_TYPE = '/errors/already-ingested'

export class FileUploadError extends Error {
  readonly step: UploadStep

  readonly reason: UploadErrorReason

  override readonly cause: unknown

  constructor({
    step,
    reason,
    cause,
  }: {
    step: UploadStep
    reason: UploadErrorReason
    cause?: unknown
  }) {
    super(`File upload failed at step "${step}": ${reason}`)
    this.name = 'FileUploadError'
    this.step = step
    this.reason = reason
    this.cause = cause
  }
}

// Duck-typed on purpose: Nuxt's `$fetch` (used by http-v2) bundles its own
// `ofetch` copy, so `instanceof FetchError` fails for backend errors.
function getStatusCode(error: unknown) {
  if (typeof error !== 'object' || error === null) return undefined
  if (!('statusCode' in error) || typeof error.statusCode !== 'number') {
    return undefined
  }

  return error.statusCode
}

function getProblemType(error: unknown) {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return undefined
  }

  const { data } = error

  return typeof data === 'object' && data !== null && 'type' in data
    ? data.type
    : undefined
}

function getBackendReason(error: unknown, step: 'config' | 'sign') {
  const statusCode = getStatusCode(error)

  if (statusCode === undefined) return 'NETWORK'
  if (
    step === 'sign' &&
    statusCode === 403 &&
    getProblemType(error) === UPLOAD_DISABLED_PROBLEM_TYPE
  ) {
    return 'UPLOAD_DISABLED'
  }
  if (statusCode === 401 || statusCode === 403) return 'FORBIDDEN'
  if (statusCode === 404) return 'UNKNOWN_USE_CASE'

  return 'SERVICE'
}

// Ingest fails in more ways than the shared backend routes: the route can be
// missing although `/config` listed it, and a consumer/backend model mismatch
// surfaces as a `422`.
function getIngestReason(error: unknown) {
  const statusCode = getStatusCode(error)

  if (statusCode === undefined) return 'NETWORK'
  if (statusCode === 401 || statusCode === 403) return 'FORBIDDEN'
  if (statusCode === 404) {
    return getProblemType(error) === UNKNOWN_USE_CASE_PROBLEM_TYPE
      ? 'UNKNOWN_USE_CASE'
      : 'ROUTE_UNAVAILABLE'
  }
  if (statusCode === 422) return 'INVALID_REQUEST'

  return 'SERVICE'
}

function getCloudinaryReason(error: unknown) {
  const statusCode = getStatusCode(error)

  if (statusCode === undefined) return 'NETWORK'
  if (statusCode === 400) return 'INVALID_FILE'
  if (statusCode === 401) return 'SIGNATURE_REJECTED'

  return 'SERVICE'
}

// The ingest route answers `409 /errors/already-ingested` when the record is
// already stored, which the call resolves as a success. Require both the `409`
// status and the exact problem type: another status carrying the type, or a
// `409` without it, must still surface as a failure.
export function isAlreadyIngestedError(error: unknown) {
  return (
    getStatusCode(error) === 409 &&
    getProblemType(error) === ALREADY_INGESTED_PROBLEM_TYPE
  )
}

function getReason(error: unknown, step: UploadStep) {
  if (step === 'upload') return getCloudinaryReason(error)
  if (step === 'ingest') return getIngestReason(error)

  return getBackendReason(error, step)
}

export function toFileUploadError(error: unknown, step: UploadStep) {
  if (error instanceof FileUploadError) return error

  return new FileUploadError({
    cause: error,
    reason: getReason(error, step),
    step,
  })
}
